import { useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useRequestWithFeedback } from "../../../composites/request/request.hooks";
import { useObjectMemo } from "../../../hooks";
import { actions as accountSortingActions } from "../slices/accountSorting.slice";

import { actions as accountsActions } from "./slice";
import { MAX_PAGE_SIZE } from "./consts";

import type { BootstrapFetchRequest } from "../../bootstrap";
import type { RootState } from "../store";
import type { AccountSorting } from "../../../../types/models/v1";
import type { NetworkPayload } from "../../../composites/request/request.types";

type AnyAccountsData = { accounts: API.AnyAccount[] };

export const useFetchAccounts = () => {
  const dispatch = useDispatch();
  const hasData = useSelector(
    (state: RootState) =>
      state.accounts.didFetch || Object.keys(state.accounts.byId).length > 0,
  );
  const { send, loading, status } = useRequestWithFeedback<AnyAccountsData>();
  const fetchAccounts = useCallback(() => {
    send({
      action: {
        url: `accounts/?per_page=${MAX_PAGE_SIZE}`,
        options: {
          method: "GET",
          timeoutMilliseconds: 90 * 1000,
        },
      },
      onData: (data) => {
        dispatch(accountsActions.receive(data, true));
      },
    });
  }, [dispatch, send]);

  return useObjectMemo({
    send: fetchAccounts,
    loading,
    error: status === "error",
    hasData,
  }) satisfies BootstrapFetchRequest;
};

type SortingResponse = {
  // eslint-disable-next-line camelcase
  account_sorting: AccountSorting[];
};

export const useFetchAccountSorting = () => {
  const dispatch = useDispatch();

  const { send, loading, status } = useRequestWithFeedback<SortingResponse>();

  const fetchSorting = useCallback(() => {
    const action: NetworkPayload = {
      url: "accounts/sorting",
      options: {
        method: "GET",
      },
    };

    const onData = (data: SortingResponse) => {
      dispatch(accountSortingActions.receive(data));
    };

    send({
      action,
      onData,
    });
  }, [dispatch, send]);

  return useObjectMemo({
    send: fetchSorting,
    loading,
    error: status === "error",
  });
};
